import React, { useEffect, useState, useCallback } from 'react';
import { Field, useFormikContext } from 'formik';
import { PROMPT_TYPE } from 'react-covideo-ai-assist';
import { Flex, Gap } from 'lib/components/styles/layout';
import { MagicButton } from 'lib/components/MagicButton';
import { ScriptSelector } from './components/ScriptSelector';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';
import { Button } from 'react-covideo-common';
import { MdSave } from 'react-icons/md';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { CREATE_NEW_SCRIPT_ID } from '../../aiSpotlight/modal/constants';
import { ICreateSpotlightFormikValues } from '../../aiSpotlight/modal/types';
import { TeleprompterResponse } from 'lib/api/teleprompter/types';
import { SCRIPT_MAX_LENGTH } from '../../aiSpotlight/modal/CreateAISpotlightModal';

interface ScriptGeneratorProps {
  placeholder?: string;
  selectOptions: TeleprompterResponse[];
  isLoading: boolean;
}

export const ScriptGenerator = ({
  placeholder = 'Type or paste your script here...',
  selectOptions,
  isLoading,
}: ScriptGeneratorProps) => {
  const { setFieldValue, values, handleSubmit, isSubmitting, touched } =
    useFormikContext<ICreateSpotlightFormikValues>();
  const [isNameInputVisible, setIsNameInputVisible] = useState(false);

  const isMaxLengthReached =
    SCRIPT_MAX_LENGTH !== undefined &&
    String(values.text).length >= SCRIPT_MAX_LENGTH;

  const isDisabled = isLoading || isSubmitting;
  const isCreateNewScript = values.scriptId === CREATE_NEW_SCRIPT_ID;
  const isScriptActionsDisabled =
    !touched.text || (values.text || '').trim() === '';
  const isSaveDisabled = (values.title || '').trim() === '' || isDisabled;

  const handleCancelNameInput = useCallback(() => {
    setIsNameInputVisible(false);
    setFieldValue('action', undefined);
    setFieldValue('title', '');
  }, [setFieldValue]);

  const handleFinalSubmit = () => {
    handleSubmit();
    setIsNameInputVisible(false);
  };

  const handleGeneratedContent = (generatedContent: string) => {
    setFieldValue('text', generatedContent);
  };

  const handleSelectChange = (selectedOption: TeleprompterResponse) => {
    const scriptId = selectedOption?.scriptId ?? null;

    if (scriptId === values.scriptId) {
      return;
    }

    const text =
      selectedOption?.scriptId === CREATE_NEW_SCRIPT_ID
        ? ''
        : selectedOption?.content || '';

    setFieldValue('scriptId', Number(scriptId));
    setFieldValue('text', text);
  };

  const handleShowNameInput = () => {
    setFieldValue('action', 'scriptUpsert');
    setIsNameInputVisible(true);
  };

  const handleUpdateScript = () => {
    setFieldValue('action', 'scriptUpsert');
    handleSubmit();
  };

  useEffect(() => {
    if (!isCreateNewScript) {
      handleCancelNameInput();
    }
  }, [isCreateNewScript, handleCancelNameInput]);

  const selectedValue =
    selectOptions.find(option => option.scriptId === values.scriptId) || null;

  return (
    <Flex gap='4px'>
      <Gap flexWrap='no-wrap' alignItems='center'>
        <ScriptSelector
          options={selectOptions}
          value={selectedValue}
          onChange={handleSelectChange}
          isLoading={isDisabled}
        />
        <MagicButton
          userInput={values?.text || ''}
          defaultPromptType={PROMPT_TYPE.TELEPROPTER_SCRIPT}
          handleSubmitGeneratedContent={handleGeneratedContent}
          disabled={isDisabled}
        />
      </Gap>
      <FormikTextAreaField
        name='text'
        placeholder={placeholder}
        label={''}
        isRequired={false}
        extendStyles={{ textAreaStyles: { minHeight: '212px' } }}
        showCounter
        maxLength={SCRIPT_MAX_LENGTH}
        disabled={isDisabled}
      />

      {!isNameInputVisible && (
        <>
          {isCreateNewScript ? (
            <Button
              disabled={
                isScriptActionsDisabled || isDisabled || isMaxLengthReached
              }
              variant='secondary'
              text='Create Script'
              icon={<MdSave size={18} />}
              onClick={handleShowNameInput}
              type='button'
              value='script'
              extendStyles={{ container: { width: '160px' } }}
            />
          ) : (
            <Button
              disabled={
                isScriptActionsDisabled || isDisabled || isMaxLengthReached
              }
              variant='secondary'
              text='Update Script'
              icon={<MdSave size={18} />}
              onClick={handleUpdateScript}
              type='submit'
              value='script'
              extendStyles={{ container: { width: '160px' } }}
            />
          )}
        </>
      )}

      {isNameInputVisible && isCreateNewScript && (
        <Flex flexDirection='row' gap='8px' alignItems='center'>
          <FormikInputField
            type='text'
            placeholder='Enter script name'
            name='title'
            isRequired={false}
            disabled={isDisabled}
          />
          <Button
            disabled={isSaveDisabled || isLoading || isMaxLengthReached}
            variant='primary'
            text='Save'
            onClick={handleFinalSubmit}
            type='submit'
            value='script'
            style={{ alignSelf: 'flex-start' }}
          />
          <Button
            variant='secondary'
            text='Cancel'
            onClick={handleCancelNameInput}
            type='button'
            style={{ alignSelf: 'flex-start' }}
            disabled={isDisabled || isMaxLengthReached}
          />
        </Flex>
      )}
      <Field type='hidden' name='action' />
    </Flex>
  );
};
